import { createFileRoute } from "@tanstack/react-router";
import type { AABB2 } from "geom/aabb2Types";
import type { DebugPoint2Options, DebugPolyLine2Options, DebugTextOptions } from "geom/debugTypes";
import { polyLine2AABB2 } from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { polyLine2New } from "geom/polyLine2Types";
import { offsetPolyLine2SSkDebug } from "geom/straightSkeleton/straightSkeleton";
import Canvas2D from "lib/components/debug/Canvas2D";
import { useEffect, useState } from "react";
import { straightSkeletonTests } from "test/unit/geom/straightSkeleton/fixtures";
import { round } from "test/unit/visual/visual.util";
import Input from "ui/forms/Input";

const spacing = 200;
const size = 90;
const min = -50;
const buf = spacing / 2;

interface TestResult {
  id: string;
  generator: PolyLine2;
  offsets: number[];
  text: DebugTextOptions[];
  points: DebugPoint2Options[];
  lines: DebugPolyLine2Options[];
  aabb: AABB2;
}

function Offset() {
  const [offset, setOffset] = useState<number>(0);
  const [dynamicLines, setDynamicLines] = useState<{ [key: string]: DebugPolyLine2Options[] }>({});

  const [results, setResults] = useState<{ [key: string]: TestResult }>({});

  useEffect(() => {
    const results: { [key: string]: TestResult } = {};

    let num = 0;
    for (const { id, generator, offsets } of straightSkeletonTests(size)) {
      const pl = polyLine2New(generator);
      const aabb = polyLine2AABB2(pl);
      const result: TestResult = { id, offsets, text: [], points: [], lines: [], generator: pl, aabb };
      result.lines.push({ pline: pl, mat: 0x000000 });
      for (const offset of offsets) {
        // function under test
        const resultLine = offsetPolyLine2SSkDebug(pl, offset, undefined, {
          repairZeroLengthEdges: true,
        });

        result.text.push({ text: `${round(offset)}`, scale: 3, x: resultLine[0], y: resultLine[1] });
        result.lines.push({ pline: resultLine, mat: 0x00cccc });
      }
      results[id] = result;
      num++;
    }

    setResults(results);
  }, []);

  useEffect(() => {
    for (const { id, generator } of Object.values(results)) {
      const cfg = { debug: { lines: [] as DebugPolyLine2Options[] }, repairZeroLengthEdges: true };

      // function under test
      const resultLine = offsetPolyLine2SSkDebug(generator, offset, undefined, cfg);

      dynamicLines[id] = [{ pline: resultLine, mat: 0xff0000 }];
      for (const line of cfg.debug.lines) {
        dynamicLines[id].push({ ...line, mat: 0xffdddd });
      }
    }

    setDynamicLines(dynamicLines);
  }, [dynamicLines, setDynamicLines, offset, setOffset, results]);

  return (
    <>
      <div style={{ overflow: "scroll", height: "100%", paddingTop: "50px" }}>
        {Object.keys(dynamicLines).map((id) => {
          const result = results[id];

          if (!result) {
            return null;
          }

          // move test geometry to better show on the canvas
          const lines = [...result.lines, ...dynamicLines[id]];
          const text = result.text;
          const points = result.points;

          const minAdjust: [number, number] = [-result.aabb.minX + 0.25 * spacing, -result.aabb.minY + 0.25 * spacing];
          const scale = Math.min(
            (0.5 * spacing) / (result.aabb.maxX - result.aabb.minX),
            (0.5 * spacing) / (result.aabb.maxY - result.aabb.minY),
          );

          return (
            <div key={id} style={{ display: "inline-block", margin: "5px" }}>
              <a href={`straight-skeleton-offset-single?id=${id}`}>
                <h3>{result.id}</h3>
                <Canvas2D
                  width={buf + size * 2}
                  height={buf + size * 2}
                  text={text}
                  points={points}
                  lines={lines}
                  polygons={[]}
                  offset={minAdjust}
                  scale={scale}
                />
              </a>
            </div>
          );
        })}
      </div>
      <div
        style={{
          background: "#dddddd",
          width: "calc(100% - 20px)",
          float: "left",
          position: "absolute",
          top: "5px",
          left: "5px",
        }}
      >
        <Input
          style={{ width: "100%" }}
          type="range"
          min={min}
          max={size}
          step={0.001}
          value={offset}
          onChange={(e) => setOffset(parseFloat(e.target.value))}
        />
        offset: <b>{offset}</b>
      </div>
    </>
  );
}

export const Route = createFileRoute("/debug/geom/straight-skeleton-offset")({
  component: Offset,
});
